// ติดตามการดูแลนักเรียนภาคศาสนา
//
// นักเรียนที่เข้าเกณฑ์ของแต่ละแกน (เข้าเรียน / ความประพฤติ) จะมีขั้นตอนดูแลตามระดับ
// ความเสี่ยงเป็นบันไดขั้น — ครูประจำชั้นศาสนาดูแลก่อนที่ระดับเฝ้าระวัง/เสี่ยง แล้วหัวหน้าระดับ
// เข้ามาที่ระดับเสี่ยงสูง หน้านี้เป็น checklist: ติ๊กถูกเมื่อดำเนินการแล้ว ไม่ต้องพิมพ์อะไร
// (หมายเหตุสั้น ๆ ใส่เพิ่มได้จากไอคอนโน้ต) รายชื่อนักเรียนดึงจาก risk list เดียวกับหน้า
// ระบบติดตามนักเรียน จึงใช้เกณฑ์ตัวเลขชุดเดียวกัน
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Badge, Button, Card, Col, Form, Modal, Nav, OverlayTrigger, Row, Table, Tooltip } from 'react-bootstrap'
import Swal from 'sweetalert2'
import { useReactToPrint } from 'react-to-print'
import axiosInstance from '../services/axiosInterceptor'
import { useLoading } from '../contexts/LoadingContext'
import { useLanguage } from '../contexts/LanguageContext'
import { StudentName, studentDisplayName } from '../utils/studentName'
import CareContractDoc from '../reports/print/CareContractDoc'
import th from '../locales/islamic/th'
import jawi from '../locales/islamic/jawi'
import {
  LevelBadge, StudentAvatar, aboveWatch, axisDetail, fmtDate, levelSuffix, riskLabel,
  useDarkTables, useIslamicTrackingRole, alertRequestError,
} from './trackingShared'

const API = process.env.REACT_APP_API
const FILTERS_KEY = 'islamicCareTracking.filters'

const escHtml = (str) => String(str ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

const WHO_BG = { advisor: 'info', head: 'primary' }

const getSavedFilters = () => {
  try { return JSON.parse(sessionStorage.getItem(FILTERS_KEY) || '{}') } catch { return {} }
}

// ความคืบหน้าหลังติ๊ก — server เก็บตัวเลขของแกน ณ ตอนติ๊กไว้ แล้วเทียบกับตัวเลขปัจจุบัน
// (attendance = % ขาด, behavior = คะแนนที่เหลือ) ลูกศรเขียวขึ้น = ดีขึ้น, แดงลง = แย่ลง
const TREND_ICON = { better: 'ri-arrow-up-line', worse: 'ri-arrow-down-line', same: 'ri-subtract-line' }
const TREND_CLS = { better: 'text-success', worse: 'text-danger', same: 'text-secondary' }
const fmtMetric = (v, unit) => `${v}${unit === '%' ? '%' : ''}`

const trendLabel = (t, trend) => ({
  better: t.careTracking.trendBetter,
  worse: t.careTracking.trendWorse,
  same: t.careTracking.trendSame,
}[trend] || t.careTracking.trendSame)

const ProgressHint = ({ t, progress }) => {
  if (!progress) return null
  const trend = progress.trend || 'same'
  return (
    <div
      className={`fs-xs text-nowrap lh-1 mt-1 ${TREND_CLS[trend]}`}
      title={`${trendLabel(t, trend)} — ${t.careTracking.tickedAt} ${fmtMetric(progress.before, progress.unit)} → ${t.careTracking.nowAt} ${fmtMetric(progress.now, progress.unit)}`}
    >
      <i className={`${TREND_ICON[trend]} fw-bold`} />
      {fmtMetric(progress.before, progress.unit)} → {fmtMetric(progress.now, progress.unit)}
    </div>
  )
}

// บรรทัด "ความคืบหน้า" ใน tooltip ของช่องติ๊ก
const progressText = (t, axisKey, progress) => {
  if (!progress) return ''
  const before = axisDetail(t, axisKey, progress.snapshot) || fmtMetric(progress.before, progress.unit)
  const delta = progress.delta
    ? ` ${progress.delta > 0 ? '+' : ''}${progress.delta}${progress.unit === '%' ? '%' : ''}`
    : ''
  return `\n${t.careTracking.tickedAt}: ${before}\n${t.careTracking.nowAt}: ${fmtMetric(progress.now, progress.unit)} (${trendLabel(t, progress.trend)}${delta})`
}

// เบอร์โทรผู้ปกครอง — โชว์เฉพาะแถวที่เลยระดับเฝ้าระวัง เพราะขั้นถัดไปของนักเรียน
// กลุ่มนี้คือโทรแจ้งผู้ปกครอง (server ส่ง contact มาให้เฉพาะแถวเหล่านั้น)
const ContactPhone = ({ t, contact, level }) => {
  if (!contact?.phone) {
    return <span className="fs-xs text-secondary">{aboveWatch(level) ? t.careTracking.noPhone : '—'}</span>
  }
  const who = [contact.name, contact.relation].filter(Boolean).join(' · ')
  return (
    <>
      <a href={`tel:${contact.phone.replace(/[^0-9+]/g, '')}`} className="fs-sm text-nowrap" dir="ltr">
        <i className="ri-phone-line me-1" />{contact.phone}
      </a>
      {who && <div className="fs-xs text-secondary text-truncate" title={who}>{who}</div>}
    </>
  )
}

// ช่องติ๊กหนึ่งขั้นตอนของนักเรียนหนึ่งคน
//  - ติ๊กแล้ว: tooltip บอกใคร/เมื่อไร + ตัวเลขความคืบหน้าใต้ช่อง
//  - ยังไม่ถึงระดับของขั้นนี้และยังไม่เคยติ๊ก: ไม่มีช่องติ๊ก แสดง "ยังไม่ถึง" — กันติ๊กล่วงหน้า
//    (ถ้าเคยติ๊กตอนเข้าเกณฑ์แล้วระดับลดลง ยังเห็นเครื่องหมายถูกและแก้ไขได้)
//  - allowed = false: ผู้ใช้คนนี้ไม่มีสิทธิ์ติ๊กขั้นนี้ → ดูได้อย่างเดียว
const StepCheck = ({ t, axisKey, step, action, required, allowed, busy, onToggle, onNote }) => {
  const tc = t.careTracking
  const done = !!action
  // ยกเลิกติ๊กแล้วติ๊กใหม่ = ลบแถวแล้วเขียนใหม่ ตัวเลขตอนติ๊ก/ผู้บันทึก/วันที่จึงกลายเป็นของ
  // วันที่ติ๊กใหม่ ช่องที่ตัวเลขขยับไปจากตอนติ๊กแล้วจึงล็อกไว้ — server เป็นคนตัดสิน (ส่งมาใน
  // action.locked) และปฏิเสธซ้ำอีกชั้นตอน POST ตรงนี้แค่ปิดช่องไม่ให้กดเปล่าๆ
  // คนที่ติ๊กไม่ได้อยู่แล้ว (ดูอย่างเดียว) ไม่ต้องรู้เรื่องล็อก ช่องปิดอยู่แล้วด้วย allowed
  const locked = done && allowed && !!action.locked
  const whoLabel = step.who === 'advisor' ? t.risk.whoAdvisor : t.risk.whoHead
  const tip = done
    ? `${action.done_by_name || tc.noRecorder} · ${fmtDate(action.done_at)}${action.note ? `\n${action.note}` : ''}${progressText(t, axisKey, action.progress)}${locked ? `\n${tc.lockedTip}` : ''}`
    : !allowed ? tc.notAllowedTip(whoLabel)
    : tc.pendingTip(step.label)

  if (!done && !required) {
    return (
      <OverlayTrigger placement="top" overlay={<Tooltip>{tc.notYetTip(riskLabel(t, step.level))}</Tooltip>}>
        <span className="fs-xs text-secondary fst-italic" onClick={e => e.stopPropagation()}>{tc.notYet}</span>
      </OverlayTrigger>
    )
  }

  return (
    <div className="d-inline-flex flex-column align-items-center" onClick={e => e.stopPropagation()}>
      <div className="d-inline-flex align-items-center gap-1">
        <OverlayTrigger placement="top" overlay={<Tooltip style={{ whiteSpace: 'pre-line' }}>{tip}</Tooltip>}>
          <span className="d-inline-block">
            <Form.Check
              type="checkbox"
              className="m-0"
              checked={done}
              disabled={busy || !allowed || locked}
              onChange={() => onToggle(!done)}
              style={{ transform: 'scale(1.35)', cursor: busy ? 'wait' : (allowed && !locked) ? 'pointer' : 'not-allowed' }}
            />
          </span>
        </OverlayTrigger>
        {done && allowed && (
          <button
            type="button"
            className={`btn btn-link btn-sm p-0 lh-1 ${action.note ? 'text-primary' : 'text-secondary'}`}
            title={action.note || tc.addNote}
            onClick={onNote}
          >
            <i className={action.note ? 'ri-sticky-note-fill' : 'ri-sticky-note-line'} />
          </button>
        )}
        {locked && <i className="ri-lock-line fs-xs text-secondary" title={tc.lockedTip} />}
      </div>
      {done && <ProgressHint t={t} progress={action.progress} />}
    </div>
  )
}

export default function IslamicCareTracking() {
  const { setLoading } = useLoading()
  const { lang } = useLanguage()
  const t = lang === 'th' ? th : jawi
  const tc = t.careTracking
  const navigate = useNavigate()
  const { roleLabel } = useIslamicTrackingRole(t)
  useDarkTables()

  const [yearId, setYearId] = useState(() => localStorage.getItem('yearId') || null)
  const [yearList, setYearList] = useState([])
  const [classList, setClassList] = useState([])
  const [roomList, setRoomList] = useState([])
  const [scope, setScope] = useState({ levelKeys: [], canTickAny: false })

  const [axis, setAxis] = useState(() => getSavedFilters().axis ?? 'attendance')
  const [classId, setClassId] = useState(() => getSavedFilters().classId ?? '')
  const [roomId, setRoomId] = useState(() => getSavedFilters().roomId ?? '')
  const [gender, setGender] = useState(() => getSavedFilters().gender ?? '')
  const [level, setLevel] = useState(() => getSavedFilters().level ?? '')        // '' | red | orange | yellow
  const [status, setStatus] = useState(() => getSavedFilters().status ?? 'pending') // pending | complete | ''

  const [steps, setSteps] = useState([])
  const [rows, setRows] = useState([])
  const [summary, setSummary] = useState({ total: 0, pending: 0, complete: 0, byLevel: {} })
  const [pendingByAxis, setPendingByAxis] = useState({})
  const [busyKey, setBusyKey] = useState(null) // `${st_id}:${step}` ที่กำลังบันทึก
  const [contract, setContract] = useState(null)
  const contractRef = useRef(null)

  // แท็บ = แกนความเสี่ยง — คำอธิบายเกณฑ์ใช้ค่าเดียวกับ islamicTrackingModel.RISK
  const AXIS_TABS = useMemo(() => [
    { key: 'attendance', label: t.risk.axisAttendance, icon: 'ri-calendar-close-line', criteria: tc.criteriaAttendance },
    { key: 'behavior',   label: t.risk.axisBehavior,   icon: 'ri-shield-user-line',    criteria: tc.criteriaBehavior },
  ], [t, tc])
  const axisMeta = AXIS_TABS.find(x => x.key === axis) || AXIS_TABS[0]

  // ป้ายหัวตาราง — สื่อว่าขั้นนั้นต้องทำอะไร แทนคำว่าระดับความเสี่ยง
  const stepHeadLabel = { yellow: tc.stepHeadYellow, orange: tc.stepHeadOrange }

  // หัวหน้าวิชาการศาสนา (57) ดูสถานะได้ทั้งภาคแต่ติ๊กไม่ได้ — server คืน canTick ว่างอยู่แล้ว
  // ตรงนี้แค่เปลี่ยนชื่อหน้า/คำอธิบายให้ตรงกับที่เขาทำได้จริง
  const readOnly = !scope.canTickAny
  const pageTitle = readOnly ? tc.pageTitleRead : tc.pageTitle

  useEffect(() => {
    axiosInstance.get(`${API}/getStudyYear`)
      .then(res => {
        const list = res.data || []
        setYearList(list)
        let id = localStorage.getItem('yearId')
        if (!id && list.length > 0) { id = list[0].year_id; localStorage.setItem('yearId', id) }
        setYearId(id)
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (!yearId) return
    axiosInstance.get(`${API}/islamic/tracking/filterOptions/${yearId}`)
      .then(res => {
        setClassList(res.data?.classes || [])
        setRoomList(res.data?.rooms || [])
        setScope(res.data?.scope || { levelKeys: [], canTickAny: false })
      })
      .catch(() => {})
  }, [yearId])

  // ตัวกรองที่จำไว้อาจชี้ไปชั้น/ห้องนอกสิทธิ์ — ล้างทิ้ง
  useEffect(() => {
    if (classList.length && classId && !classList.some(c => String(c.ic_id) === String(classId))) setClassId('')
  }, [classList, classId])
  useEffect(() => {
    if (roomList.length && roomId && !roomList.some(r => String(r.ir_id) === String(roomId))) setRoomId('')
  }, [roomList, roomId])
  useEffect(() => {
    if (!gender || !roomId || !roomList.length) return
    const r = roomList.find(x => String(x.ir_id) === String(roomId))
    if (r && r.ir_gender !== gender) setRoomId('')
  }, [gender, roomId, roomList])
  const isFirstClassRender = useRef(true)
  useEffect(() => {
    if (isFirstClassRender.current) { isFirstClassRender.current = false; return }
    setRoomId('')
  }, [classId])

  useEffect(() => {
    sessionStorage.setItem(FILTERS_KEY, JSON.stringify({ axis, classId, roomId, gender, level, status }))
  }, [axis, classId, roomId, gender, level, status])

  // ตัวกรองร่วม (ไม่รวมแกน/ระดับ/สถานะ) — ใช้ทั้งตารางและตัวเลขบนแท็บ
  const scopeQuery = useMemo(() => {
    const p = new URLSearchParams()
    if (classId) p.append('classId', classId)
    if (roomId) p.append('roomId', roomId)
    if (gender) p.append('gender', gender)
    return p.toString()
  }, [classId, roomId, gender])

  // ปุ่ม "ลองใหม่อีกครั้ง" ตอนคิวเต็มต้องเรียก fetchData ตัวล่าสุด จึงถือผ่าน ref
  const fetchDataRef = useRef(null)

  // คำขอเดียวได้ทั้งตารางของแกนที่เลือกและตัวเลข "ยังไม่ครบ" ของทุกแท็บ
  const fetchData = useCallback(() => {
    if (!yearId) return
    setLoading(true)
    const p = new URLSearchParams(scopeQuery)
    p.append('axis', axis)
    if (level) p.append('level', level)
    if (status) p.append('status', status)
    axiosInstance.get(`${API}/islamic/tracking/care/${yearId}?${p.toString()}`)
      .then(res => {
        setSteps(res.data?.steps || [])
        setRows(res.data?.rows || [])
        setSummary(res.data?.summary || { total: 0, pending: 0, complete: 0, byLevel: {} })
        setPendingByAxis(res.data?.pendingByAxis || {})
      })
      .catch(err => alertRequestError(t, err, tc.loadError, { retry: true })
        .then(result => { if (result.isConfirmed && err?.response?.status === 503) fetchDataRef.current?.() }))
      .finally(() => setLoading(false))
  }, [yearId, scopeQuery, axis, level, status, setLoading, t, tc])

  useEffect(() => { fetchDataRef.current = fetchData; fetchData() }, [fetchData])

  const filteredRooms = useMemo(
    () => roomList.filter(r =>
      (!classId || String(r.ic_id) === String(classId)) &&
      (!gender || r.ir_gender === gender)),
    [roomList, classId, gender]
  )

  // อัปเดตแถวในหน้าโดยไม่โหลดใหม่ทั้งตาราง — คำนวณ missing/complete ใหม่จาก required
  const applyAction = useCallback((stId, stepKey, action) => {
    setRows(prev => prev.map(r => {
      if (r.st_id !== stId) return r
      const actions = { ...r.actions }
      if (action) actions[stepKey] = action; else delete actions[stepKey]
      const missing = r.required.filter(k => !actions[k])
      return { ...r, actions, missing, complete: missing.length === 0 }
    }))
  }, [])

  // ติ๊ก 1 ครั้งกระทบตัวเลขบนแท็บได้แค่แกนที่กำลังดูอยู่ และเปลี่ยนได้ทีละ 1 คน
  // จึงบวก/ลบเองได้ ไม่ต้องยิงคิวรีทั้งภาคใหม่เพียงเพื่ออัปเดตเลขบนแท็บ
  const bumpPending = useCallback((row, stepKey, action) => {
    const next = { ...(row.actions || {}) }
    if (action) next[stepKey] = action; else delete next[stepKey]
    const completeAfter = (row.required || []).filter(k => !next[k]).length === 0
    if (Boolean(row.complete) === completeAfter) return
    setPendingByAxis(prev => ({
      ...prev,
      [axis]: Math.max(0, Number(prev[axis] || 0) + (completeAfter ? -1 : 1)),
    }))
  }, [axis])

  const toggleStep = async (row, step, done) => {
    if (!yearId) return
    setBusyKey(`${row.st_id}:${step.key}`)
    try {
      const res = await axiosInstance.post(`${API}/islamic/tracking/care/${yearId}/${row.st_id}`, {
        axis, step: step.key, done, level: row.axis?.level,
      })
      applyAction(row.st_id, step.key, res.data?.action || null)
      bumpPending(row, step.key, res.data?.action || null)
    } catch (err) {
      alertRequestError(t, err, tc.saveError)
    } finally {
      setBusyKey(null)
    }
  }

  const editNote = async (row, step) => {
    const current = row.actions?.[step.key]?.note || ''
    const { value, isConfirmed } = await Swal.fire({
      title: step.short,
      text: tc.noteTitle(studentDisplayName(row, lang)),
      input: 'text',
      inputValue: current,
      inputAttributes: { maxlength: 255 },
      showCancelButton: true,
      confirmButtonText: tc.noteSave,
      cancelButtonText: tc.noteCancel,
    })
    if (!isConfirmed) return
    try {
      const res = await axiosInstance.post(`${API}/islamic/tracking/care/${yearId}/${row.st_id}`, {
        axis, step: step.key, done: true, level: row.axis?.level, note: value || '',
      })
      applyAction(row.st_id, step.key, res.data?.action || null)
      bumpPending(row, step.key, res.data?.action || null)
    } catch (err) {
      alertRequestError(t, err, tc.saveError)
    }
  }

  // พิมพ์หนังสือสัญญา — ใช้แบบฟอร์มเดียวกับภาคสามัญ (มีทั้งช่องห้องสามัญและห้องศาสนา)
  // ช่องที่ระบบไม่มีข้อมูลจะพิมพ์เป็นเส้นประให้เขียนมือ
  const openContract = async (row) => {
    setLoading(true)
    try {
      const res = await axiosInstance.get(`${API}/islamic/tracking/care/contract/${yearId}/${row.st_id}`)
      setContract(res.data)
    } catch (err) {
      alertRequestError(t, err, tc.contractError)
    } finally {
      setLoading(false)
    }
  }

  // ขนาดกระดาษต้องส่งผ่าน pageStyle ไม่ใช่ @page ในไฟล์ CSS เพราะ CRA รวม CSS ทั้งแอป
  // เป็นสไตล์ชีตเดียว กฎ @page จึงหลุดไปทับหน้าอื่นทั้งระบบ
  const printContract = useReactToPrint({
    contentRef: contractRef,
    documentTitle: contract
      ? `${th.careTracking.contractTitle} ${contract.st_idnumber || ''} ${contract.st_name || ''}`.trim()
      : th.careTracking.contractTitle,
    pageStyle: '@page { size: A4 portrait; margin: 0; }',
  })

  // พิมพ์ตารางติดตามการดูแลตามตัวกรองปัจจุบัน — A4 แนวนอน เปิดหน้าต่างใหม่แล้วสั่งพิมพ์
  // เอกสารที่พิมพ์เข้าแฟ้มนักเรียนเป็นภาษาไทยเสมอ (เหมือนหนังสือสัญญา) ไม่ตามภาษาเมนู
  // ขั้นที่ยังไม่ได้ติ๊กพิมพ์เป็น ☐ ไว้ให้ติ๊กด้วยมือในที่ประชุม ส่วนขั้นที่ยังไม่ถึงระดับพิมพ์
  // เป็น - ไม่ใช่ช่องว่าง เพื่อไม่ให้เข้าใจผิดว่าลืมทำ
  const printChecklist = () => {
    if (!rows.length) return
    const tcTh = th.careTracking
    const axisLabel = axis === 'attendance' ? th.risk.axisAttendance : th.risk.axisBehavior
    const headLabel = { yellow: tcTh.stepHeadYellow, orange: tcTh.stepHeadOrange }
    const classLabel = classId
      ? (classList.find(c => String(c.ic_id) === String(classId))?.ic_class || classId)
      : th.tracking.printClassAll
    const room = roomId ? roomList.find(r => String(r.ir_id) === String(roomId)) : null
    const conditions = [
      { m: th.common.male, f: th.common.female }[gender] || th.tracking.printGenderAll,
      classLabel,
      room ? `${room.ic_class}/${room.ir_no}` : th.tracking.printRoomAll,
      level ? riskLabel(th, level) : tcTh.filterLevelAll,
      { pending: tcTh.statusPending, complete: tcTh.statusComplete }[status] || tcTh.statusAll,
    ].join(' | ')

    const stepCell = (r, s) => {
      const a = r.actions?.[s.key]
      if (!a) return r.required.includes(s.key) ? '<span class="box">☐</span>' : '-'
      const sub = [
        fmtDate(a.done_at),
        a.progress ? `${fmtMetric(a.progress.before, a.progress.unit)} → ${fmtMetric(a.progress.now, a.progress.unit)}` : null,
      ].filter(Boolean).join(' · ')
      return `<span class="tick">✔</span> ${escHtml(a.done_by_name || '')}`
        + `<div class="sub">${escHtml(sub)}</div>`
        + (a.note ? `<div class="sub">${escHtml(a.note)}</div>` : '')
    }

    const contactCell = (c) => (c?.phone
      ? `${escHtml(c.phone)}<div class="sub">${escHtml([c.name, c.relation].filter(Boolean).join(' · '))}</div>`
      : '-')

    const body = rows.map((r, idx) => `<tr>
        <td class="c">${idx + 1}</td>
        <td class="c">${escHtml(r.st_idnumber)}</td>
        <td>${escHtml(r.st_name)}</td>
        <td class="c">${escHtml(r.class_room)}</td>
        <td class="c">${escHtml(r.regular_room || '-')}</td>
        <td class="c">${escHtml(riskLabel(th, r.axis?.level))}</td>
        <td>${escHtml(axisDetail(th, axis, r.axis))}</td>
        <td class="c">${contactCell(r.contact)}</td>
        ${steps.map(s => `<td class="c">${stepCell(r, s)}</td>`).join('')}
        <td class="c">${r.complete ? tcTh.complete : tcTh.missing(r.missing.length, r.required.length)}</td>
      </tr>`).join('')

    // คอลัมน์ขั้นตอนมีกี่ช่องขึ้นกับแกน จึงแบ่งความกว้างที่เหลือให้เท่าๆ กัน
    const stepWidth = (27 / Math.max(steps.length, 1)).toFixed(2)
    const cols = ['4', '8', '16', '6', '6', '6', '12', '9', ...steps.map(() => stepWidth), '6']
    const title = `${tcTh.printTitle} — ${axisLabel}${yearLabel ? ` ${th.common.studyYear(yearLabel)}` : ''}`
    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
      <title>${escHtml(title)}</title>
      <style>
        @page { size: A4 landscape; margin: 8mm; }
        * { box-sizing: border-box; }
        html, body { margin: 0; padding: 0; }
        body { font-family: 'Cordia New', 'TH Sarabun New', Tahoma, Arial, sans-serif; font-size: 14px; color: #000; }
        h3, h4 { text-align: center; margin: 0 0 4px 0; }
        h3 { font-size: 18px; }
        h4 { font-size: 15px; font-weight: normal; }
        .meta { font-size: 12px; margin: 6px 0; display: flex; justify-content: space-between; }
        .legend { font-size: 12px; margin-bottom: 4px; }
        table { border-collapse: collapse; width: 100%; table-layout: fixed; }
        th, td { border: 1px solid #000; padding: 2px 3px; overflow: hidden; word-wrap: break-word; }
        thead { display: table-header-group; }
        thead th { text-align: center; font-size: 12px; background: #f0f0f0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        tbody tr { page-break-inside: avoid; }
        .c { text-align: center; }
        .sub { font-size: 11px; color: #444; }
        .tick { font-weight: bold; }
        .box { font-size: 16px; }
        .sign { margin-top: 14px; font-size: 12px; text-align: right; }
      </style></head>
      <body onload="window.print()">
        <h3>โรงเรียนวัฒนธรรมอิสลาม</h3>
        <h4>${escHtml(title)}</h4>
        <div class="meta">
          <span>${th.tracking.printConditions}: ${escHtml(conditions)}</span>
          <span>${escHtml(tcTh.printSummary(rows.length, summary.complete || 0, summary.pending || 0))}</span>
        </div>
        <div class="legend">${escHtml(tcTh.printLegend)}</div>
        <table>
          <colgroup>${cols.map(w => `<col style="width:${w}%;" />`).join('')}</colgroup>
          <thead><tr>
            <th>#</th><th>เลขประจำตัว</th><th>ชื่อ-สกุล</th><th>ห้องศาสนา</th><th>ห้องสามัญ</th>
            <th>ระดับ</th><th>ข้อมูล</th><th>เบอร์ผู้ปกครอง</th>
            ${steps.map(s => `<th>${escHtml(s.short)}<div class="sub">${escHtml(headLabel[s.level] || '')} · ${escHtml(s.who === 'advisor' ? th.risk.whoAdvisor : th.risk.whoHead)}</div></th>`).join('')}
            <th>สถานะ</th>
          </tr></thead>
          <tbody>${body}</tbody>
        </table>
        <div class="sign">${th.tracking.printAt} ${new Date().toLocaleString('th-TH')}</div>
      </body></html>`

    const win = window.open('', '_blank', 'width=1200,height=900')
    if (!win) return Swal.fire(t.tracking.printBlocked, t.tracking.printBlockedText, 'warning')
    win.document.write(html)
    win.document.close()
  }

  // เมื่อกรอง "ยังไม่ครบ" อยู่ แถวที่ติ๊กครบแล้วยังคงอยู่จนกว่าจะโหลดใหม่ — ตั้งใจให้เป็น
  // แบบนี้ เพื่อไม่ให้แถวหายไปทันทีที่ติ๊ก (ติ๊กผิดยังแก้ได้)
  const summaryCards = [
    { key: 'pending',  label: tc.cardPending,  value: summary.pending,  bg: 'danger',  icon: 'ri-error-warning-line' },
    { key: 'complete', label: tc.cardComplete, value: summary.complete, bg: 'success', icon: 'ri-checkbox-circle-line' },
    { key: 'red',      label: t.risk.red,      value: summary.byLevel?.red,    bg: 'danger',  icon: 'ri-alarm-warning-line', filter: 'red' },
    { key: 'orange',   label: t.risk.orange,   value: summary.byLevel?.orange, bg: 'orange',  icon: 'ri-error-warning-line', filter: 'orange', hide: !steps.some(s => s.level === 'orange') },
    { key: 'yellow',   label: t.risk.yellow,   value: summary.byLevel?.yellow, bg: 'warning', icon: 'ri-eye-line', filter: 'yellow' },
  ].filter(c => !c.hide)

  const levelOptions = [['', tc.filterLevelAll], ...['red', 'orange', 'yellow']
    .filter(l => steps.some(s => s.level === l))
    .map(l => [l, riskLabel(t, l)])]

  const yearLabel = yearList.find(y => String(y.year_id) === String(yearId))?.year_year || ''
  const goToStudent = (stId) => navigate(`/islamic/student-tracking/${stId}?yearId=${yearId}`)

  const stepCheckFor = (r, s) => (
    <StepCheck
      t={t}
      axisKey={axis}
      step={s}
      action={r.actions?.[s.key]}
      required={r.required.includes(s.key)}
      allowed={(r.canTick || []).includes(s.key)}
      busy={busyKey === `${r.st_id}:${s.key}`}
      onToggle={(done) => toggleStep(r, s, done)}
      onNote={() => editNote(r, s)}
    />
  )

  const statusBadge = (r) => (r.complete
    ? <Badge bg="success" className="fs-xs"><i className="ri-check-line me-1" />{tc.complete}</Badge>
    : <Badge bg="light" text="dark" className="fs-xs border">{tc.missing(r.missing.length, r.required.length)}</Badge>)

  return (
    <React.Fragment>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4">
        <div>
          <div className="fs-sm text-secondary mb-1">{roleLabel} · {tc.breadcrumb}</div>
          <h4 className="main-title mb-0">
            <i className="ri-checkbox-multiple-line me-2 text-primary" />
            {pageTitle}{levelSuffix(t, scope.levelKeys)}
            {yearLabel && <span className="text-secondary fs-5 fw-normal"> {t.common.studyYear(yearLabel)}</span>}
          </h4>
          <p className="text-secondary fs-sm mb-0">{readOnly ? tc.subtitleRead : tc.subtitle}</p>
        </div>
        <select
          className="form-select w-auto"
          value={yearId || ''}
          onChange={(e) => { localStorage.setItem('yearId', e.target.value); setYearId(e.target.value) }}
        >
          {yearList.map(y => (
            <option key={y.year_id} value={y.year_id}>{t.common.studyYear(y.year_year)}</option>
          ))}
        </select>
      </div>

      {/* แท็บแกนความเสี่ยง */}
      <Nav variant="pills" className="nav-sm gap-1 mb-3 flex-wrap">
        {AXIS_TABS.map(x => (
          <Nav.Item key={x.key}>
            <Nav.Link active={axis === x.key} onClick={() => { setAxis(x.key); setLevel('') }} style={{ cursor: 'pointer' }}>
              <i className={`${x.icon} me-1`} />{x.label}
              {Number(pendingByAxis[x.key] || 0) > 0 && (
                <Badge bg={axis === x.key ? 'light' : 'danger'} text={axis === x.key ? 'dark' : undefined} pill className="ms-2">
                  {pendingByAxis[x.key]}
                </Badge>
              )}
            </Nav.Link>
          </Nav.Item>
        ))}
      </Nav>

      {/* บันไดขั้นตอนดูแลของแท็บนี้ */}
      <Card className="card-one mb-3">
        <Card.Body className="py-3">
          <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
            <span className="fw-semibold"><i className="ri-route-line me-1 text-primary" />{tc.stepsTitle} — {axisMeta.label}</span>
            <span className="text-secondary fs-xs">{tc.criteria}: {axisMeta.criteria}</span>
          </div>
          <div className="d-flex flex-wrap gap-2">
            {steps.map((s, i) => (
              <div key={s.key} className="d-flex align-items-center gap-2">
                {i > 0 && <i className="ri-arrow-right-line text-secondary d-none d-md-inline" />}
                <div className="border rounded px-2 py-1 d-flex align-items-center gap-2" style={{ background: 'var(--bs-tertiary-bg, rgba(0,0,0,.02))' }}>
                  <LevelBadge level={s.level}>{riskLabel(t, s.level)}</LevelBadge>
                  <Badge bg={WHO_BG[s.who] || 'secondary'} className="fs-xs">
                    {s.who === 'advisor' ? t.risk.whoAdvisor : t.risk.whoHead}
                  </Badge>
                  <span className="fs-sm">{s.label}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="fs-xs text-secondary mt-2">{tc.cumulativeNote}</div>
          <div className="fs-xs text-secondary mt-1">
            {tc.progressNote}
            <span className="text-success ms-1"><i className="ri-arrow-up-line fw-bold" />{tc.trendBetter}</span>
            <span className="text-danger ms-2"><i className="ri-arrow-down-line fw-bold" />{tc.trendWorse}</span>
            <span className="ms-2"><i className="ri-subtract-line fw-bold" />{tc.trendSame}</span>
            <span className="ms-2">{tc.progressLegacy}</span>
          </div>
          <div className="fs-xs text-secondary mt-1">
            <i className="ri-lock-line me-1" />{tc.lockedNote}
          </div>
        </Card.Body>
      </Card>

      {/* การ์ดสรุป (กดการ์ดระดับเพื่อกรอง) */}
      <Row className="g-3 mb-3">
        {summaryCards.map(c => (
          <Col key={c.key} xs={6} md={4} xl>
            <Card
              className={`card-one h-100${c.filter && level === c.filter ? ' border-primary' : ''}`}
              style={c.filter ? { cursor: 'pointer' } : undefined}
              onClick={c.filter ? () => setLevel(level === c.filter ? '' : c.filter) : undefined}
            >
              <Card.Body className="d-flex align-items-center gap-3 py-3">
                <div className={`d-flex align-items-center justify-content-center rounded bg-${c.bg} text-white flex-shrink-0`} style={{ width: 44, height: 44 }}>
                  <i className={`${c.icon} fs-4`} />
                </div>
                <div>
                  <h3 className="mb-0">{c.value ?? 0}</h3>
                  <span className="text-secondary fs-sm">{c.label}</span>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* ตัวกรอง */}
      <Card className="card-one mb-3">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col xs={6} md={2}>
              <Form.Label className="fs-sm mb-1">{t.tracking.filterGender}</Form.Label>
              <Form.Select size="sm" value={gender} onChange={e => setGender(e.target.value)}>
                <option value="">{t.tracking.filterAll}</option>
                <option value="m">{t.common.male}</option>
                <option value="f">{t.common.female}</option>
              </Form.Select>
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="fs-sm mb-1">{t.tracking.filterClass}</Form.Label>
              <Form.Select size="sm" value={classId} onChange={e => setClassId(e.target.value)}>
                <option value="">{t.tracking.filterAll}</option>
                {classList.map(c => <option key={c.ic_id} value={c.ic_id}>{c.ic_class}</option>)}
              </Form.Select>
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="fs-sm mb-1">{t.tracking.filterRoom}</Form.Label>
              <Form.Select size="sm" value={roomId} onChange={e => setRoomId(e.target.value)} disabled={!classId}>
                <option value="">{t.tracking.filterAll}</option>
                {filteredRooms.map(r => <option key={r.ir_id} value={r.ir_id}>{r.ic_class}/{r.ir_no}</option>)}
              </Form.Select>
            </Col>
            <Col xs={12} md={3}>
              <Form.Label className="fs-sm mb-1">{tc.filterLevel}</Form.Label>
              <div className="btn-group btn-group-sm w-100">
                {levelOptions.map(([v, label]) => (
                  <button key={v || 'all'} type="button" className={`btn btn${level === v ? '' : '-outline'}-primary`} onClick={() => setLevel(v)}>{label}</button>
                ))}
              </div>
            </Col>
            <Col xs={12} md={3}>
              <Form.Label className="fs-sm mb-1">{tc.filterStatus}</Form.Label>
              <div className="btn-group btn-group-sm w-100">
                {[['pending', tc.statusPending], ['complete', tc.statusComplete], ['', tc.statusAll]].map(([v, label]) => (
                  <button key={v || 'all'} type="button" className={`btn btn${status === v ? '' : '-outline'}-primary`} onClick={() => setStatus(v)}>{label}</button>
                ))}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* รายการนักเรียน */}
      <Card className="card-one">
        <Card.Header className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
          <div className="d-flex align-items-center gap-2">
            <Card.Title as="h6" className="mb-0">{tc.listTitle} — {axisMeta.label}</Card.Title>
            <Badge bg="light" text="dark" className="fs-sm">{tc.count(rows.length)}</Badge>
          </div>
          <div className="d-flex align-items-center gap-2">
            <span className="fs-xs text-secondary d-none d-lg-inline">{tc.clickNameHint}</span>
            <Button size="sm" variant="outline-primary" disabled={!rows.length} onClick={printChecklist}>
              <i className="ri-printer-line me-1" />{t.tracking.printBtn}
            </Button>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {rows.length === 0 ? (
            <div className="text-center text-secondary py-5">
              <i className="ri-shield-check-line fs-2 d-block mb-2" />
              {tc.empty}
            </div>
          ) : (
            <>
              {/* จอใหญ่: ตาราง */}
              <div className="d-none d-md-block" style={{ maxHeight: 640, overflowY: 'auto' }}>
                <Table hover size="sm" className="mb-0 align-middle">
                  <thead className="table-secondary sticky-top">
                    <tr>
                      <th className="text-center" style={{ width: 44 }}>{tc.colNo}</th>
                      <th style={{ minWidth: 200 }}>{tc.colStudent}</th>
                      <th className="text-center" style={{ width: 80 }}>{tc.colRoom}</th>
                      <th style={{ minWidth: 190 }}>{tc.colLevelInfo}</th>
                      <th style={{ minWidth: 140 }}>{tc.colContact}</th>
                      {steps.map(s => (
                        <th key={s.key} className="text-center" style={{ minWidth: 120 }}>
                          <div>{s.short}</div>
                          <div className="fw-normal fs-xs">
                            <LevelBadge level={s.level}>{stepHeadLabel[s.level]}</LevelBadge>{' '}
                            <span className="text-secondary">{s.who === 'advisor' ? t.risk.whoAdvisor : t.risk.whoHead}</span>
                          </div>
                        </th>
                      ))}
                      <th className="text-center" style={{ width: 100 }}>{tc.colStatus}</th>
                      {!readOnly && <th className="text-center" style={{ width: 76 }}>{tc.colContract}</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, idx) => (
                      <tr key={r.st_id} className={r.complete ? 'table-success' : undefined}>
                        <td className="text-center text-secondary">{idx + 1}</td>
                        <td>
                          <div className="d-flex align-items-center gap-2" style={{ cursor: 'pointer' }} onClick={() => goToStudent(r.st_id)}>
                            <StudentAvatar photo={r.st_photo} name={studentDisplayName(r, lang)} zoom={false} />
                            <div>
                              <div className="fw-semibold"><StudentName student={r} lang={lang} /></div>
                              <div className="fs-xs text-secondary">{r.st_idnumber}</div>
                            </div>
                          </div>
                        </td>
                        <td className="text-center fs-sm">{r.class_room}</td>
                        <td>
                          <LevelBadge level={r.axis?.level}>{riskLabel(t, r.axis?.level)}</LevelBadge>
                          <div className="fs-xs text-secondary mt-1">{axisDetail(t, axis, r.axis)}</div>
                        </td>
                        <td><ContactPhone t={t} contact={r.contact} level={r.axis?.level} /></td>
                        {steps.map(s => <td key={s.key} className="text-center">{stepCheckFor(r, s)}</td>)}
                        <td className="text-center">{statusBadge(r)}</td>
                        {!readOnly && (
                          <td className="text-center">
                            <OverlayTrigger placement="top" overlay={<Tooltip>{tc.contractBtn}</Tooltip>}>
                              <Button variant="outline-secondary" size="sm" className="px-2 py-0"
                                onClick={(e) => { e.stopPropagation(); openContract(r) }}>
                                <i className="ri-printer-line" />
                              </Button>
                            </OverlayTrigger>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* มือถือ: การ์ด */}
              <div className="d-md-none p-3 d-flex flex-column gap-3">
                {rows.map((r, idx) => (
                  <div key={r.st_id} className={`border rounded p-3${r.complete ? ' border-success' : ''}`}>
                    <div className="d-flex align-items-start gap-2" onClick={() => goToStudent(r.st_id)}>
                      <StudentAvatar photo={r.st_photo} name={studentDisplayName(r, lang)} size={40} zoom={false} />
                      <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <div className="d-flex align-items-center justify-content-between gap-2">
                          <div className="fw-semibold text-truncate"><StudentName student={r} lang={lang} /></div>
                          <LevelBadge level={r.axis?.level}>{riskLabel(t, r.axis?.level)}</LevelBadge>
                        </div>
                        <div className="fs-xs text-secondary">#{idx + 1} · {r.st_idnumber} · {r.class_room}</div>
                        <div className="fs-xs text-secondary">{axisDetail(t, axis, r.axis)}</div>
                      </div>
                    </div>
                    {r.contact?.phone && (
                      <div className="mt-2"><ContactPhone t={t} contact={r.contact} level={r.axis?.level} /></div>
                    )}
                    <div className="mt-2 pt-2 border-top d-flex flex-column gap-2">
                      {steps.map(s => (
                        <div key={s.key} className="d-flex align-items-center justify-content-between gap-2">
                          <div className="fs-sm">
                            <LevelBadge level={s.level}>{stepHeadLabel[s.level]}</LevelBadge>
                            <span className="ms-1">{s.short}</span>
                            <div className="fs-xs text-secondary">{s.who === 'advisor' ? t.risk.whoAdvisor : t.risk.whoHead}</div>
                          </div>
                          {stepCheckFor(r, s)}
                        </div>
                      ))}
                      <div className="d-flex align-items-center justify-content-between gap-2">
                        {!readOnly ? (
                          <Button variant="outline-secondary" size="sm" onClick={() => openContract(r)}>
                            <i className="ri-printer-line me-1" />{tc.contractBtnShort}
                          </Button>
                        ) : <span />}
                        {statusBadge(r)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      {/* ตัวอย่างหนังสือสัญญาก่อนพิมพ์ — react-to-print พิมพ์เฉพาะกล่องนี้
          เอกสารเป็นภาษาไทยเสมอ (ใบเดียวกับภาคสามัญ) จึงตรึง dir="ltr" ไว้ ไม่ตามภาษาเมนู */}
      <Modal show={!!contract} onHide={() => setContract(null)} size="xl" fullscreen="lg-down" scrollable>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">
            <i className="ri-file-text-line me-2 text-primary" />
            {tc.contractTitle}{contract?.st_name ? ` — ${contract.st_name}` : ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: '#6c757d' }}>
          <div style={{ overflowX: 'auto' }}>
            <div ref={contractRef} dir="ltr" style={{ background: '#fff', width: '21cm', margin: '0 auto' }}>
              {contract && <CareContractDoc info={contract} />}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <span className="text-secondary fs-xs me-auto d-none d-md-inline">{tc.contractHint}</span>
          <Button variant="light" onClick={() => setContract(null)}>{tc.contractClose}</Button>
          <Button variant="primary" onClick={printContract}>
            <i className="ri-printer-line me-1" />{tc.contractPrint}
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  )
}
